import { useState } from 'react';
import { useApp, useCan } from '../../app/AppContext.js';
import { useAsync } from '../lib/useAsync.js';
import { descreverOffset } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  CardHead,
  EmptyState,
  ErrorBlock,
  KV,
  KVRow,
  Loading,
  PageHeader,
} from '../components/primitives.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal, Confirm } from '../components/Modal.js';
import { Field, TextInput } from '../components/Field.js';
import { useToast } from '../components/Toast.js';
import { IconLadder, IconPlay, IconPlus, IconTrash } from '../components/icons.js';
import type { DunningLadder, DunningPreview, DunningStep } from '../../data/port.js';

type Dialogo =
  | { tipo: 'nenhum' }
  | { tipo: 'desativar' }
  | { tipo: 'teto' }
  | { tipo: 'etapa' }
  | { tipo: 'remover'; etapa: DunningStep }
  | { tipo: 'simular' };

export function Regua() {
  const { data } = useApp();
  const toast = useToast();
  const admin = useCan('console-admin');
  const [dialogo, setDialogo] = useState<Dialogo>({ tipo: 'nenhum' });
  const [ocupado, setOcupado] = useState(false);

  const estado = useAsync<DunningLadder>(() => data.getLadder(), [data]);
  const fechar = () => setDialogo({ tipo: 'nenhum' });

  const ativar = async () => {
    setOcupado(true);
    try {
      const r = await data.setLadderActive(true);
      const ativas = r.steps.filter((e) => e.active).length;
      toast.success('Régua ativada', ativas === 0 ? 'Atenção: nenhuma etapa ativa.' : `${ativas} etapas ativas`);
      estado.reload();
    } catch (e) {
      toast.error('Falha ao ativar', e instanceof Error ? e.message : undefined);
    } finally {
      setOcupado(false);
    }
  };

  const desativar = async () => {
    setOcupado(true);
    try {
      await data.setLadderActive(false);
      toast.info('Régua desativada', 'Nenhum disparo sai daqui até reativar.');
      estado.reload();
      fechar();
    } catch (e) {
      toast.error('Falha ao desativar', e instanceof Error ? e.message : undefined);
    } finally {
      setOcupado(false);
    }
  };

  const alternarEtapa = async (etapa: DunningStep) => {
    try {
      const atualizada = await data.setStepActive(etapa.id, !etapa.active);
      if (!atualizada) {
        toast.error(`Etapa #${etapa.id} não encontrada`);
        return;
      }
      toast.success(`Etapa #${etapa.id} ${atualizada.active ? 'ligada' : 'desligada'}`);
      estado.reload();
    } catch (e) {
      toast.error('Falha ao alterar etapa', e instanceof Error ? e.message : undefined);
    }
  };

  const removerEtapa = async (etapa: DunningStep) => {
    setOcupado(true);
    try {
      const ok = await data.removeStep(etapa.id);
      if (ok) {
        toast.success(`Etapa #${etapa.id} removida`);
        estado.reload();
      } else {
        toast.error(`Etapa #${etapa.id} não encontrada`);
      }
      fechar();
    } finally {
      setOcupado(false);
    }
  };

  const colunas: Column<DunningStep>[] = [
    { key: 'id', header: '#', num: true, cell: (e) => e.id },
    { key: 'quando', header: 'Quando', cell: (e) => descreverOffset(e.offsetDays) },
    { key: 'etapa', header: 'Etapa', cell: (e) => <span className="td--strong">{e.label}</span> },
    { key: 'ativa', header: 'Ativa', cell: (e) => (e.active ? <Badge tone="ok">sim</Badge> : <Badge tone="neutral">não</Badge>) },
    ...(admin
      ? [
          {
            key: 'acoes',
            header: '',
            align: 'right' as const,
            cell: (e: DunningStep) => (
              <div className="row" style={{ justifyContent: 'flex-end', gap: '0.4rem' }}>
                <Button variant="ghost" size="sm" onClick={() => void alternarEtapa(e)}>
                  {e.active ? 'desligar' : 'ligar'}
                </Button>
                <Button variant="danger-ghost" size="sm" onClick={() => setDialogo({ tipo: 'remover', etapa: e })} title="Remover">
                  <IconTrash size={14} />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const regua = estado.data;

  return (
    <>
      <PageHeader
        title="Régua de cobrança"
        desc="As etapas que disparam webhooks de cobrança em relação ao vencimento."
        actions={
          <>
            <Button variant="ghost" onClick={() => setDialogo({ tipo: 'simular' })}>
              <IconPlay size={15} /> Simular
            </Button>
            {admin && regua && (
              regua.active ? (
                <Button variant="danger-ghost" onClick={() => setDialogo({ tipo: 'desativar' })} loading={ocupado}>
                  Desativar
                </Button>
              ) : (
                <Button variant="primary" onClick={() => void ativar()} loading={ocupado}>
                  Ativar régua
                </Button>
              )
            )}
          </>
        }
      />

      {estado.loading && !regua && <Card><Loading /></Card>}
      {estado.error && <ErrorBlock message={estado.error} />}

      {regua && (
        <>
          <Card>
            <div className="card__body">
              <div className="row row--between wrap" style={{ gap: '1rem' }}>
                <KV>
                  <KVRow k="Régua">{regua.name}</KVRow>
                  <KVRow k="Estado">
                    <Badge tone={regua.active ? 'ok' : 'warn'} dot>
                      {regua.active ? 'ATIVA' : 'DESATIVADA'}
                    </Badge>
                  </KVRow>
                  <KVRow k="Teto diário">
                    <span className="num">{regua.dailyCap}</span> disparos/dia
                    {admin && (
                      <Button variant="ghost" size="sm" onClick={() => setDialogo({ tipo: 'teto' })} title="Editar teto">
                        editar
                      </Button>
                    )}
                  </KVRow>
                </KV>
              </div>
            </div>
          </Card>

          <Card>
            <CardHead
              title="Etapas"
              sub="Cada etapa dispara um webhook — quem envia é a automação"
              actions={
                admin && (
                  <Button variant="primary" size="sm" onClick={() => setDialogo({ tipo: 'etapa' })}>
                    <IconPlus size={15} /> Nova etapa
                  </Button>
                )
              }
            />
            <div className="card__body--flush">
              {regua.steps.length === 0 ? (
                <EmptyState
                  icon={<IconLadder size={40} />}
                  title="Sem etapas"
                  desc="A régua não dispara nada até ter ao menos uma etapa ativa."
                />
              ) : (
                <DataTable columns={colunas} rows={regua.steps} keyOf={(e) => String(e.id)} />
              )}
            </div>
          </Card>
        </>
      )}

      {dialogo.tipo === 'desativar' && (
        <Confirm
          title="Desativar régua"
          danger
          confirmLabel="Desativar"
          busy={ocupado}
          message="Nenhuma cobrança será disparada até alguém reativar. Confirma?"
          onConfirm={() => void desativar()}
          onClose={fechar}
        />
      )}
      {dialogo.tipo === 'remover' && (
        <Confirm
          title={`Remover etapa #${dialogo.etapa.id}`}
          danger
          confirmLabel="Remover"
          busy={ocupado}
          message={`A etapa "${dialogo.etapa.label}" (${descreverOffset(dialogo.etapa.offsetDays)}) será removida da régua.`}
          onConfirm={() => void removerEtapa(dialogo.etapa)}
          onClose={fechar}
        />
      )}
      {dialogo.tipo === 'teto' && regua && (
        <TetoDialog atual={regua.dailyCap} onClose={fechar} onSalvo={() => { fechar(); estado.reload(); }} />
      )}
      {dialogo.tipo === 'etapa' && (
        <EtapaDialog onClose={fechar} onCriada={() => { fechar(); estado.reload(); }} />
      )}
      {dialogo.tipo === 'simular' && <SimularDialog onClose={fechar} />}
    </>
  );
}

function TetoDialog({ atual, onClose, onSalvo }: { atual: number; onClose: () => void; onSalvo: () => void }) {
  const { data } = useApp();
  const toast = useToast();
  const [valor, setValor] = useState(String(atual));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!/^\d+$/.test(valor.trim())) {
      setErro('O teto precisa ser um inteiro ≥ 0.');
      return;
    }
    setSalvando(true);
    try {
      const r = await data.setDailyCap(Number(valor));
      toast.success(`Teto diário: ${r.dailyCap} disparos`, r.dailyCap === 0 ? 'Teto zero, na prática, desliga a régua.' : undefined);
      onSalvo();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      title="Teto diário da régua"
      sub="Quantos disparos a régua pode fazer por dia."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={salvando}>Cancelar</Button>
          <Button variant="primary" onClick={() => void salvar()} loading={salvando}>Salvar</Button>
        </>
      }
    >
      {erro && <div className="notice notice--danger">{erro}</div>}
      <Field label="Disparos por dia" required>
        <TextInput value={valor} onChange={setValor} mono inputMode="numeric" autoFocus onEnter={() => void salvar()} />
      </Field>
    </Modal>
  );
}

function EtapaDialog({ onClose, onCriada }: { onClose: () => void; onCriada: () => void }) {
  const { data } = useApp();
  const toast = useToast();
  const [dias, setDias] = useState('');
  const [rotulo, setRotulo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const criar = async () => {
    if (!/^-?\d+$/.test(dias.trim())) {
      setErro('Dias precisa ser um inteiro (negativo = antes do vencimento).');
      return;
    }
    if (!rotulo.trim()) {
      setErro('Informe o rótulo da etapa.');
      return;
    }
    setSalvando(true);
    try {
      const e = await data.addStep({ offsetDays: Number(dias), label: rotulo.trim() });
      toast.success(`Etapa #${e.id} "${e.label}" criada`, descreverOffset(e.offsetDays));
      onCriada();
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao criar etapa.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      title="Nova etapa da régua"
      sub="Dias negativo = antes do vencimento. Ex.: -5 avisa 5 dias antes."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={salvando}>Cancelar</Button>
          <Button variant="primary" onClick={() => void criar()} loading={salvando}>Criar etapa</Button>
        </>
      }
    >
      {erro && <div className="notice notice--danger">{erro}</div>}
      <div className="form-grid">
        <div className="form-row">
          <Field label="Dias do vencimento" required hint="-5, 0, 3…">
            <TextInput value={dias} onChange={setDias} mono inputMode="numeric" autoFocus placeholder="-5" />
          </Field>
          <Field label="Rótulo" required hint="a automação usa para escolher o texto">
            <TextInput value={rotulo} onChange={setRotulo} placeholder="lembrete_previo" onEnter={() => void criar()} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function SimularDialog({ onClose }: { onClose: () => void }) {
  const { data } = useApp();
  const [dataSim, setDataSim] = useState('2026-07-20');
  const [resultado, setResultado] = useState<
    { estado: 'inicial' } | { estado: 'carregando' } | { estado: 'erro'; mensagem: string } | { estado: 'ok'; previa: DunningPreview }
  >({ estado: 'inicial' });

  const simular = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dataSim)) {
      setResultado({ estado: 'erro', mensagem: 'Use o formato AAAA-MM-DD.' });
      return;
    }
    setResultado({ estado: 'carregando' });
    try {
      const previa = await data.previewDunning(dataSim);
      setResultado({ estado: 'ok', previa });
    } catch (e) {
      setResultado({ estado: 'erro', mensagem: e instanceof Error ? e.message : 'Falha ao simular.' });
    }
  };

  return (
    <Modal
      title="Simular a régua"
      sub="Mostra o que a régua faria numa data, sem disparar nada."
      onClose={onClose}
      wide
      footer={<Button variant="ghost" onClick={onClose}>Fechar</Button>}
    >
      <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem', marginBottom: '1rem' }}>
        <Field label="Data (AAAA-MM-DD)">
          <TextInput value={dataSim} onChange={setDataSim} mono onEnter={() => void simular()} />
        </Field>
        <Button variant="primary" onClick={() => void simular()} loading={resultado.estado === 'carregando'}>
          <IconPlay size={15} /> Simular
        </Button>
      </div>

      {resultado.estado === 'erro' && <div className="notice notice--danger">{resultado.mensagem}</div>}
      {resultado.estado === 'ok' && <PreviaResultado previa={resultado.previa} />}
    </Modal>
  );
}

function PreviaResultado({ previa }: { previa: DunningPreview }) {
  return (
    <>
      <div className="grid grid--stats" style={{ marginBottom: '1rem' }}>
        <Stat2 label="Varridos" value={previa.scanned} />
        <Stat2 label="Devidos" value={previa.due} />
        <Stat2 label="Já disparados" value={previa.alreadyRun} />
        <Stat2 label="A disparar" value={previa.toDispatch} destaque />
      </div>
      {previa.detail.length > 0 ? (
        <DataTable
          columns={[
            { key: 't', header: 'Título', cell: (d: { invoiceId: string }) => <span className="num">{d.invoiceId}</span> },
            { key: 'c', header: 'Cliente', cell: (d: { customerName: string }) => d.customerName },
            { key: 's', header: 'Etapa', cell: (d: { step: string }) => d.step },
          ]}
          rows={previa.detail}
          keyOf={(d) => `${d.invoiceId}:${d.step}`}
        />
      ) : (
        <p className="muted">Nada seria disparado nessa data.</p>
      )}
      <p className="faint" style={{ marginTop: '0.75rem' }}>Simulação: nenhum webhook sai daqui.</p>
    </>
  );
}

function Stat2({ label, value, destaque }: { label: string; value: number; destaque?: boolean }) {
  return (
    <div className="card stat">
      <span className={`stat__accent stat__accent--${destaque ? 'info' : 'ok'}`} />
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={{ fontSize: '1.6rem' }}>{value}</div>
    </div>
  );
}
